import { SymbolLayerSpecification } from 'mapbox-gl'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'

import { EuiPanel, EuiTitle, EuiText, EuiInMemoryTable, EuiBasicTableColumn, SortDirection, EuiSpacer, EuiEmptyPrompt, EuiFlexGroup, EuiFlexItem, EuiLink } from '@elastic/eui'

import { useOptimalColumnsWidth } from 'utils/useOptimalColumnsWidth'

import { useGetBillboardsByZipCodesQuery } from '../../../../../api/billboardApi'
import { BillboardGeo } from '../../../../../api/entities/Billboard'
import { MasterCampaignPlanCustomSection } from '../../../../../api/entities/MasterCampaignPlan'
import { MasterCampaignStub } from '../../../../../api/entities/MasterCampaignStub'
import { MapBoxContainer } from '../../../../../components/mapBox/MapBoxContainer'
import { useMapBox } from '../../../../../components/mapBox/useMapBox'
import { calculateCenterPoint, calculateOptimalZoom, GeoPoint } from '../../../../../utils/mapUtils'
import { useBillboardZipCodes } from '../../../../../utils/useProjectionsUtils'

export interface BillboardLocationsProps {
  masterCampaignStub: MasterCampaignStub
  section?: MasterCampaignPlanCustomSection
}

const BillboardLocations: FC<BillboardLocationsProps> = ({ masterCampaignStub, section }) => {
  const sectionTitle = section?.title ?? 'Billboard Locations'
  const sectionContent = section?.content ?? 'Billboard ads will be placed programmatically via live auction. Placements are targeted by zip code and will appear on a selection of the following billboard sites. Choice of specific billboards within the set may be possible upon request, based on availability.'
  const sectionShowTitle = section?.showTitle ?? true
  const { mapContainerRef, initializeMap, setMarkers, flyTo, setInitialCenterAndZoom, returnToCenter, hasInitialCenter, isReady, isLoading, hasError, error, mapInstance } = useMapBox()
  const [selectedBillboardId, setSelectedBillboardId] = useState<string | null>(null)
  const [singleBillboardMode, setSingleBillboardMode] = useState<boolean>(false)

  const handleViewBillboardFromGrid = useCallback(
    (billboard: BillboardGeo) => {
      setSingleBillboardMode(true)
      setSelectedBillboardId(billboard.shortId)
      flyTo([billboard.longitude, billboard.latitude], 15)
    },
    [mapInstance]
  )

  const handleResetView = useCallback(() => {
    setSelectedBillboardId(null)
    setSingleBillboardMode(false)
    returnToCenter()
  }, [returnToCenter])

  const zipCodes = useBillboardZipCodes(masterCampaignStub)
  const { data: billboardData } = useGetBillboardsByZipCodesQuery({ zipCodes }, { skip: zipCodes.length === 0 })

  const sortedBillboardData = useMemo(() => {
    if (!billboardData) {
      return []
    }

    return [...billboardData]
      .sort((a, b) => (b.avgDailyImpressions || 0) - (a.avgDailyImpressions || 0))
      .sort((a, b) => {
        if (!a.zipCode && !b.zipCode) return 0
        if (!a.zipCode) return 1
        if (!b.zipCode) return -1
        return String(b.zipCode).localeCompare(String(a.zipCode))
      })
  }, [billboardData])

  useEffect(() => {
    if (!isReady || !sortedBillboardData || !sortedBillboardData.length) {
      return
    }

    let markersToShow = sortedBillboardData

    if (singleBillboardMode && selectedBillboardId) {
      markersToShow = sortedBillboardData.filter(billboard => billboard.shortId === selectedBillboardId)
    }

    const bbMarkers = markersToShow.map(billboard => ({ lat: billboard.latitude, lng: billboard.longitude, id: billboard.shortId }))
    setMarkers(bbMarkers, {
      clusterSettings: {
        cluster: !singleBillboardMode,
        radius: 50
      },
      unclusteredOptions: {
        type: 'symbol',
        layout: {
          'icon-image': 'billboard',
          'icon-size': 0.3,
          'icon-allow-overlap': false
        }
      } as unknown as SymbolLayerSpecification
    })

    if (!singleBillboardMode) {
      const geoPoints = sortedBillboardData.map(billboard => ({ lat: billboard.latitude, lng: billboard.longitude }))
      const centerPoint = calculateCenterPoint(geoPoints)
      const optimalZoom = calculateOptimalZoom(geoPoints)

      if (centerPoint) {
        setInitialCenterAndZoom([centerPoint.lng, centerPoint.lat], optimalZoom)
      }
    }
  }, [sortedBillboardData, setMarkers, setInitialCenterAndZoom, isReady, singleBillboardMode, selectedBillboardId])

  const { widths, tableRef } = useOptimalColumnsWidth({
    data: billboardData || [],
    columns: [
      {
        field: 'latitude',
        headerText: 'Latitude',
        minWidth: 100,
        maxWidth: 200,
        maxWidthPercentage: 0.15,
        padding: 32
      }
    ]
  })

  const pagination = {
    initialPageSize: 10,
    pageSizeOptions: [5, 10, 20]
  }

  const billboardSorting = {
    allowNeutralSort: true,
    enableAllColumns: true,
    sort: {
      field: '',
      direction: SortDirection.DESC
    }
  }

  const billboardColumns: Array<EuiBasicTableColumn<BillboardGeo>> = [
    {
      field: 'location',
      name: 'Location',
      sortable: false,
      render: (value: any, billboard: BillboardGeo) => (
        <EuiLink onClick={() => handleViewBillboardFromGrid(billboard)} aria-label={`View billboard ${billboard.shortId} on map`}>
          View on Map
        </EuiLink>
      )
    },
    {
      field: 'route',
      name: 'Roadway',
      sortable: true
    },
    {
      field: 'city',
      name: 'City',
      sortable: true
    },
    {
      field: 'zipCode',
      name: 'Zip',
      sortable: true
    },
    {
      field: 'mediaOwner',
      name: 'Provider',
      sortable: true
    },
    {
      field: 'shortId',
      name: 'Billboard ID',
      sortable: true
    }
  ]

  if (section?.hideAll === true) {
    return null
  }

  if (!billboardData || billboardData.length === 0) {
    return (
      <EuiPanel paddingSize='l'>
        {sectionShowTitle && (
          <EuiTitle size='m'>
            <h2>{sectionTitle}</h2>
          </EuiTitle>
        )}
        <EuiSpacer size='l' />
        <EuiEmptyPrompt
          iconType='visMapRegion'
          title={<h3>No billboard locations found</h3>}
          body={
            <p>
              No billboard inventory was found for the targeted zip codes in this campaign plan.
              {zipCodes.length === 0 ? ' Please ensure your campaign has location targeting configured.' : ` Searched in ${zipCodes.length} zip code${zipCodes.length > 1 ? 's' : ''}: ${zipCodes.join(', ')}`}
            </p>
          }
        />
      </EuiPanel>
    )
  }

  return (
    <EuiPanel paddingSize='l'>
      {sectionShowTitle && (
        <EuiTitle size='m'>
          <h2>{sectionTitle}</h2>
        </EuiTitle>
      )}
      <EuiSpacer size='s' />
      <EuiText size='m' color='subdued' style={{ whiteSpace: 'pre-wrap' }}>
        {sectionContent}
      </EuiText>

      <EuiSpacer size='l' />

      <div ref={tableRef}>
        <EuiInMemoryTable items={sortedBillboardData} columns={billboardColumns} pagination={pagination} sorting={billboardSorting} responsive={true} hasActions={false} />
      </div>

      <EuiSpacer size='l' />

      <div style={{ maxWidth: 1000, height: 400, marginLeft: 'auto', marginRight: 'auto', marginBottom: '2rem' }}>
        <EuiFlexGroup justifyContent='spaceBetween' gutterSize='s'>
          <EuiFlexItem grow={false}>
            <EuiLink color='success' onClick={handleResetView} disabled={!hasInitialCenter} aria-label='Show all billboards'>
              Show All
            </EuiLink>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiLink color='success' onClick={handleResetView} disabled={!hasInitialCenter} aria-label='Reset map view'>
              Reset View
            </EuiLink>
          </EuiFlexItem>
        </EuiFlexGroup>

        <EuiSpacer size='s' />

        <MapBoxContainer mapContainerRef={mapContainerRef} initializeMap={initializeMap} isReady={isReady} isLoading={isLoading} hasError={hasError} error={error} />
      </div>
    </EuiPanel>
  )
}

export default BillboardLocations
